import { Tab, Tabs } from '@material-ui/core';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { getTextDetailsPanelTabLabel, TEXT_DETAILS_PANEL_TAB } from 'src/data/ui';

import { Benchmark } from '../../components/Benchmark';
import { BlockedMessage } from '../../components/BlockedMessage';
import { BrandTerms } from '../../components/BrandTerms';
import { ChannelPolicy } from '../../components/ChannelPolicy';
import { CopyInput } from '../../components/CopyInput';
import { Format } from '../../components/Format';
import { Histograms } from '../../components/Histograms';
import { ScoreAdditionalFactors } from '../../components/ScoreAdditionalFactors';
import { ScoringMethod } from '../../components/ScoringMethod';
import { TalkingPoints } from '../../components/TalkingPoints';
import { ToneOfVoice } from '../../components/ToneOfVoice';
import { DetectedBrandTermsProvider, preparePropTypesFromChildComponent } from '../../lib';

import { useStyles } from './useStyles';

export const DocumentInsightsPanel = ({
  className,
  classes,
  tabProps,
  copyInputProps,
  scoreDetailsProps,
  scoreAdditionalFactorsProps,
  audienceChartProps,
  histogramAgeScoreProps,
  histogramGenderScoreProps,
  talkingPointsProps,
  brandTermsProps,
  channelPolicyProps,
  formatProps,
  blockedProps,
  benchmarkProps,
  toneOfVoiceProps,
}) => {
  const styles = useStyles({ classes });

  const tabsVisible = tabProps?.showTabs || false;
  // if "tab props" are present, then we should render only "selected tab", by default all panel content is visible
  const performanceTabVisible = tabsVisible
    ? tabProps?.selectedTab === TEXT_DETAILS_PANEL_TAB.performance
    : tabProps?.availableTabs.includes(TEXT_DETAILS_PANEL_TAB.performance);
  const brandPolicyTabVisible = tabsVisible
    ? tabProps?.selectedTab === TEXT_DETAILS_PANEL_TAB.brandPolicy
    : tabProps?.availableTabs.includes(TEXT_DETAILS_PANEL_TAB.brandPolicy);

  return (
    <DetectedBrandTermsProvider
      onApplyBrandTerm={brandTermsProps?.onApplyBrandTerm}
      appliedDetectedTermIds={brandTermsProps?.appliedDetectedTermIds}
    >
      <div
        className={cx(styles.root, className)}
        data-testid="DocumentInsightsPanel"
        data-component="DocumentInsightsPanel"
      >
        {(scoreDetailsProps || copyInputProps || tabsVisible) && (
          <div
            className={cx(styles.detailsHeader, {
              [styles.noPaddingBottom]: tabsVisible,
            })}
          >
            {scoreDetailsProps && <ScoringMethod {...scoreDetailsProps} />}

            {copyInputProps && <CopyInput {...copyInputProps} />}

            {tabsVisible ? (
              <Tabs
                value={tabProps.selectedTab}
                onChange={(event, newTab) => tabProps.onSelectTab(newTab)}
                indicatorColor="primary"
              >
                {tabProps.availableTabs.map((tab) => (
                  <Tab
                    className={styles.tab}
                    key={tab}
                    label={getTextDetailsPanelTabLabel(tab)}
                    value={tab}
                  />
                ))}
              </Tabs>
            ) : (
              <CustomTypography variant={TYPOGRAPHY_VARIANT.medium14}>
                {getTextDetailsPanelTabLabel(TEXT_DETAILS_PANEL_TAB.brandPolicy)}
              </CustomTypography>
            )}
          </div>
        )}

        <div className={styles.detailsBody}>
          {performanceTabVisible && (
            <>
              {benchmarkProps && (
                <Benchmark
                  classes={{ root: styles.groupContainer, boxGroupTitle: styles.boxGroupTitle }}
                  {...benchmarkProps}
                />
              )}

              {(audienceChartProps || histogramAgeScoreProps || histogramGenderScoreProps) && (
                <Histograms
                  classes={{ root: styles.groupContainer, boxGroupTitle: styles.boxGroupTitle }}
                  audienceChartProps={audienceChartProps}
                  histogramAgeScoreProps={histogramAgeScoreProps}
                  histogramGenderScoreProps={histogramGenderScoreProps}
                />
              )}

              {scoreAdditionalFactorsProps && (
                <ScoreAdditionalFactors {...scoreAdditionalFactorsProps} />
              )}

              {talkingPointsProps && (
                <TalkingPoints
                  classes={{ root: styles.groupContainer, boxGroupTitle: styles.boxGroupTitle }}
                  {...talkingPointsProps}
                />
              )}
            </>
          )}

          {brandPolicyTabVisible && (
            <>
              {toneOfVoiceProps && (
                <ToneOfVoice
                  classes={{ root: styles.groupContainer, boxGroupTitle: styles.boxGroupTitle }}
                  {...toneOfVoiceProps}
                />
              )}

              {brandTermsProps && (
                <BrandTerms
                  classes={{ root: styles.groupContainer, boxGroupTitle: styles.boxGroupTitle }}
                  {...brandTermsProps}
                />
              )}

              {channelPolicyProps && (
                <ChannelPolicy
                  classes={{
                    root: styles.groupContainer,
                    boxGroupTitle: styles.boxGroupTitle,
                    content: styles.channelPolicyContent,
                  }}
                  {...channelPolicyProps}
                />
              )}

              {formatProps && (
                <Format
                  classes={{
                    root: styles.groupContainer,
                    boxGroupTitle: styles.boxGroupTitle,
                  }}
                  {...formatProps}
                />
              )}
            </>
          )}
        </div>

        {blockedProps?.blocked && (
          <div className={styles.blurredOverlay}>
            <BlockedMessage
              className={styles.blockedMessage}
              {...blockedProps}
            />
          </div>
        )}
      </div>
    </DetectedBrandTermsProvider>
  );
};

DocumentInsightsPanel.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape(),
  copyInputProps: preparePropTypesFromChildComponent(CopyInput.propTypes),
  scoreDetailsProps: preparePropTypesFromChildComponent(ScoringMethod.propTypes),
  scoreAdditionalFactorsProps: preparePropTypesFromChildComponent(ScoreAdditionalFactors.propTypes),
  audienceChartProps: Histograms.propTypes.audienceChartProps,
  histogramAgeScoreProps: Histograms.propTypes.histogramAgeScoreProps,
  histogramGenderScoreProps: Histograms.propTypes.histogramGenderScoreProps,
  talkingPointsProps: preparePropTypesFromChildComponent(TalkingPoints.propTypes),
  brandTermsProps: preparePropTypesFromChildComponent(BrandTerms.propTypes),
  channelPolicyProps: preparePropTypesFromChildComponent(ChannelPolicy.propTypes),
  formatProps: preparePropTypesFromChildComponent(Format.propTypes),
  blockedProps: preparePropTypesFromChildComponent(BlockedMessage.propTypes),
  benchmarkProps: preparePropTypesFromChildComponent(Benchmark.propTypes),
  toneOfVoiceProps: preparePropTypesFromChildComponent(ToneOfVoice.propTypes),
  tabProps: PropTypes.shape({
    showTabs: PropTypes.bool,
    selectedTab: PropTypes.string,
    onSelectTab: PropTypes.func,
    availableTabs: PropTypes.arrayOf(PropTypes.string),
  }),
  generationTool: PropTypes.string,
};

DocumentInsightsPanel.defaultProps = {
  className: null,
  classes: null,
  copyInputProps: null,
  scoreDetailsProps: null,
  scoreAdditionalFactorsProps: null,
  audienceChartProps: null,
  histogramAgeScoreProps: null,
  histogramGenderScoreProps: null,
  talkingPointsProps: null,
  brandTermsProps: null,
  channelPolicyProps: null,
  formatProps: null,
  blockedProps: null,
  benchmarkProps: null,
  toneOfVoiceProps: null,
  tabProps: null,
  generationTool: null,
};
